import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const navLinks = [
  { label: '首页', to: '/' },
  { label: '搜索', to: '/search' },
  { label: '收藏', to: '/favorites' },
  { label: '关于', to: '/about' },
];

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setCategoryOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCategoryOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const submitSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/70 bg-background-50/90 backdrop-blur-md">
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 items-center gap-4">
          {/* Brand */}
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500 text-foreground-950">
              <i className="ri-play-circle-fill text-xl" />
            </span>
            <span className="font-heading text-lg font-bold tracking-tight text-foreground-950">
              成人快手
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 lg:flex">
            <Link
              to="/"
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                isActive('/')
                  ? 'bg-primary-500/15 text-primary-300'
                  : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
              }`}
            >
              首页
            </Link>

            <div className="relative" ref={dropdownRef}>
              <button
                type="button"
                onClick={() => setCategoryOpen((v) => !v)}
                className={`flex items-center gap-1 rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  location.pathname.startsWith('/category')
                    ? 'bg-primary-500/15 text-primary-300'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`}
              >
                分类
                <i className={`ri-arrow-down-s-line text-base transition-transform ${categoryOpen ? 'rotate-180' : ''}`} />
              </button>
              {categoryOpen && (
                <div className="absolute left-0 top-full mt-2 w-64 animate-fade-in rounded-xl border border-background-200 bg-background-100 p-2 shadow-lg">
                  {categories.map((c) => (
                    <Link
                      key={c.id}
                      to={`/category/${c.id}`}
                      className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-background-200 hover:text-foreground-950"
                    >
                      <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-500/15 text-secondary-300">
                        <i className={`${c.icon} text-sm`} />
                      </span>
                      {c.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {navLinks.slice(1).map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? 'bg-primary-500/15 text-primary-300'
                    : 'text-foreground-700 hover:bg-background-100 hover:text-foreground-950'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Search */}
          <form
            onSubmit={submitSearch}
            className="ml-auto hidden max-w-xs flex-1 items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2 md:flex"
          >
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索视频、标签、作者"
              className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
            />
          </form>

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            className="ml-auto flex h-10 w-10 items-center justify-center rounded-lg border border-background-200 text-foreground-800 lg:hidden"
            aria-label="打开菜单"
          >
            <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
          </button>
        </div>
      </div>

      {/* Mobile panel */}
      {mobileOpen && (
        <div className="animate-fade-in border-t border-background-200/70 bg-background-50 px-4 pb-4 pt-3 lg:hidden">
          <form
            onSubmit={submitSearch}
            className="mb-3 flex items-center gap-2 rounded-full border border-background-200 bg-background-100 px-4 py-2"
          >
            <i className="ri-search-line text-base text-foreground-400" />
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索视频、标签、作者"
              className="w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
            />
          </form>
          <div className="grid grid-cols-2 gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-lg px-4 py-3 text-sm font-medium ${
                  isActive(link.to)
                    ? 'bg-primary-500/15 text-primary-300'
                    : 'bg-background-100 text-foreground-700'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
          <p className="mb-2 mt-4 text-xs font-semibold text-foreground-500">分类浏览</p>
          <div className="grid grid-cols-2 gap-2">
            {categories.map((c) => (
              <Link
                key={c.id}
                to={`/category/${c.id}`}
                className="flex items-center gap-2 rounded-lg bg-background-100 px-3 py-2 text-sm text-foreground-700"
              >
                <i className={`${c.icon} text-sm text-secondary-300`} />
                {c.name}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}